import { QuizScoreDto } from '@/api/generated';

// Colors of the admin dashboard charts, checked with the dataviz palette validator against the white card surface:
// - SERIES: categorical, adjacent pairs separable with colour-vision deficiencies; orange and aqua are below 3:1,
//   so their values are always labelled or shown in the tooltip and data view
// - ORDINAL: one hue with visible lightness steps, for ordered categories such as funnel stages (light end >= 2:1)
// - SEQUENTIAL: one hue light to dark, for magnitudes on the map and the heatmap
export const COLORS = {
  axis: '#ccc',
  grid: '#eee',
  ink: '#000',
  inkMuted: '#767676',
  inkSecondary: '#595959',
  negative: '#b5b3ab',
  negativeStrong: '#8f8d85',
  orange: '#ff7900',
  surface: '#fff',
} as const;

export const SERIES = ['#ff7900', '#2a78d6', '#1baf7a'] as const;

export const ORDINAL = ['#ff9c40', '#eb8200', '#cd6f03', '#b05c04', '#934b03', '#773a02'] as const;

export const SEQUENTIAL = [
  '#fef3e9',
  '#ffca9b',
  '#ff9d36',
  '#de7f05',
  '#b76601',
  '#914e03',
  '#6c3803',
] as const;

// Scores that send the candidate further are orange (lighter to darker), the others stay gray: the point is the cut-off
export const SCORE_COLORS: Record<QuizScoreDto, string> = {
  [QuizScoreDto.Average]: COLORS.negativeStrong,
  [QuizScoreDto.Good]: ORDINAL[0],
  [QuizScoreDto.Low]: COLORS.negative,
  [QuizScoreDto.Outstanding]: ORDINAL[4],
  [QuizScoreDto.VeryGood]: ORDINAL[2],
};

export const fontFamily = (): string =>
  typeof document === 'undefined'
    ? 'Helvetica Neue, Helvetica, Arial, sans-serif'
    : getComputedStyle(document.body).fontFamily;

export const formatNumber = (value: number, language: string, maximumFractionDigits = 0): string =>
  new Intl.NumberFormat(language, { maximumFractionDigits }).format(value);

export const formatCompact = (value: number, language: string): string =>
  new Intl.NumberFormat(language, { maximumFractionDigits: 1, notation: 'compact' }).format(value);

export const formatPercent = (part: number, whole: number, language: string): string =>
  new Intl.NumberFormat(language, {
    maximumFractionDigits: whole > 0 && part / whole < 0.1 ? 1 : 0,
    style: 'percent',
  }).format(whole > 0 ? part / whole : 0);

/**
 * Relative change of a figure against the previous period, or undefined when there is nothing to compare with
 */
export const changeOf = (current: number, previous: number | undefined): number | undefined =>
  previous === undefined || previous === 0 ? undefined : (current - previous) / previous;

// The parts of an ECharts option shared by every chart of the dashboard
export const baseOption = () => ({
  animationDuration: 900,
  animationDurationUpdate: 600,
  animationEasing: 'cubicOut' as const,
  textStyle: { color: COLORS.inkSecondary, fontFamily: fontFamily() },
  tooltip: {
    backgroundColor: COLORS.surface,
    borderColor: '#ddd',
    borderWidth: 1,
    extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.12); border-radius: 0;',
    padding: [8, 12],
    textStyle: { color: COLORS.ink, fontFamily: fontFamily(), fontSize: 13 },
  },
});

export const axisStyle = {
  axisLabel: { color: COLORS.inkMuted, fontSize: 12 },
  axisLine: { lineStyle: { color: COLORS.axis } },
  axisTick: { show: false },
  splitLine: { lineStyle: { color: COLORS.grid, type: 'solid' as const } },
};

export interface ToolboxTexts {
  close: string;
  dataView: string;
  saveImage: string;
}

// The data behind a chart as a table (also for readers who cannot tell the colors apart) and a PNG export
export const toolbox = (texts: ToolboxTexts) => ({
  emphasis: { iconStyle: { borderColor: COLORS.orange } },
  feature: {
    dataView: { lang: [texts.dataView, texts.close, ''], readOnly: true, title: texts.dataView },
    saveAsImage: { pixelRatio: 2, title: texts.saveImage },
  },
  iconStyle: { borderColor: COLORS.inkMuted },
  right: 0,
  top: 0,
});

// A short stroke of the series color before a tooltip row; the value leads, the name follows
export const tooltipRow = (color: string, value: string, name: string): string =>
  '<div style="display:flex;align-items:center;gap:8px;line-height:1.6">' +
  `<span style="display:inline-block;width:12px;height:2px;background:${color}"></span>` +
  `<strong style="min-width:3ch">${escapeHtml(value)}</strong>` +
  `<span style="color:${COLORS.inkSecondary}">${escapeHtml(name)}</span></div>`;

export const tooltipTitle = (title: string): string =>
  `<div style="font-weight:700;margin-bottom:4px">${escapeHtml(title)}</div>`;

// Labels come from the backend (dictionaries, areas, skills); ECharts tooltips are HTML strings
export const escapeHtml = (text: string): string =>
  text.replace(/[&<>"']/g, char => `&#${char.charCodeAt(0)};`);
